import { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import leadsService from '../../services/leadsService';
import './LeadImport.css';

// ─── Field options ────────────────────────────────────────────────────────────
const TARGET_FIELDS = [
  { value: 'name',       label: 'Name' },
  { value: 'phone',      label: 'Phone (primary)' },
  { value: 'whatsapp',   label: 'WhatsApp (fallback phone)' },
  { value: 'area',       label: 'Area / Location' },
  { value: 'pincode',    label: 'Pincode' },
  { value: 'notes',      label: 'Notes / Questionnaire' },
  { value: 'source_ref', label: 'Source Ref (campaign / ad)' },
  { value: 'lead_date',  label: 'Lead Date' },
  { value: 'ignore',     label: '— Ignore —' },
];

// Columns that are Meta system metadata — never shown in mapping UI, always ignored
const META_SYSTEM_COLS = new Set(['form_id', 'form_name', 'id', 'platform']);

// ─── Auto-suggest a target field from a CSV column name ──────────────────────
const autoSuggest = (col) => {
  const c = col.toLowerCase();
  if (c === 'full_name')                                   return 'name';
  if (c === 'phone_number')                                return 'phone';
  if (c.includes('whtsapp') || c.includes('whatsapp'))    return 'whatsapp';
  if (c === 'post_code' || c.includes('postcode'))         return 'pincode';
  if (c === 'campaign_name')                               return 'source_ref';
  if (c === 'created_time')                                return 'lead_date';
  if (c.includes('delivery_is') || c.includes('located') ||
      c.includes('area') && !c.includes('campaign'))       return 'area';
  if (c.includes('health_drink') || c.includes('matters_most') ||
      c.includes('question'))                              return 'notes';
  return 'ignore';
};

// ─── Data cleaning helpers ────────────────────────────────────────────────────
const cleanPhone = (raw) => {
  if (!raw) return '';
  let s = String(raw).replace(/^p:/i, '').replace(/\s+/g, '').replace(/\+/g, '');
  if (s.startsWith('91') && s.length > 10) s = s.slice(2);
  s = s.replace(/\D/g, '');
  return s.length === 10 ? s : '';
};

const cleanPincode = (raw) => {
  if (!raw) return '';
  const s = String(raw).replace(/^z:/i, '').replace(/\D/g, '').trim();
  return s.length >= 6 ? s.slice(0, 6) : '';
};

const cleanText = (raw) => {
  if (!raw) return '';
  const s = String(raw).trim();
  if (['.',  'N', 'NO', 'No', 'NO ', 'Hxcbbb'].includes(s)) return '';
  return s.replace(/_/g, ' ');
};

const cleanLeadDate = (raw) => {
  if (!raw) return null;
  try { return new Date(raw).toISOString(); } catch { return null; }
};

// ─── Apply saved/chosen mappings to a raw CSV row ────────────────────────────
const applyMappings = (rawRow, mappings) => {
  const result = { name: '', phone: '', whatsapp: '', area: '', pincode: '', notes: '', source_ref: '', lead_date: null };
  const noteParts = [];
  const srcParts  = [];

  for (const [col, field] of Object.entries(mappings)) {
    if (field === 'ignore') continue;
    const val = rawRow[col] || '';
    if (!val) continue;

    switch (field) {
      case 'name':       result.name       = cleanText(val); break;
      case 'phone':      result.phone      = cleanPhone(val); break;
      case 'whatsapp':   result.whatsapp   = cleanPhone(val); break;
      case 'area':       result.area       = cleanText(val); break;
      case 'pincode':    result.pincode    = cleanPincode(val); break;
      case 'notes':      noteParts.push(cleanText(val)); break;
      case 'source_ref': srcParts.push(cleanText(val)); break;
      case 'lead_date':  result.lead_date  = cleanLeadDate(val); break;
      default: break;
    }
  }

  result.notes      = noteParts.filter(Boolean).join(' | ');
  result.source_ref = srcParts.filter(Boolean).join(' — ');

  // Fallback: use whatsapp as phone if phone is missing/invalid
  if (!result.phone && result.whatsapp) result.phone = result.whatsapp;

  return result;
};

// ─── UTF-16 LE TSV parser (Meta Ads export format) ───────────────────────────
const parseMetaFile = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        // Strip BOM (U+FEFF) that UTF-16 files start with
        const text = e.target.result.replace(/^﻿/, '');
        const lines = text.split(/\r?\n/).filter(l => l.trim());
        if (lines.length < 2) { reject(new Error('File appears empty or has no data rows')); return; }

        const headers = lines[0].split('\t').map(h => h.trim().replace(/^"|"$/g, ''));
        const rows = [];
        for (let i = 1; i < lines.length; i++) {
          const vals = lines[i].split('\t').map(v => v.trim().replace(/^"|"$/g, ''));
          if (vals.every(v => !v)) continue;
          const obj = {};
          headers.forEach((h, idx) => { obj[h] = vals[idx] || ''; });
          rows.push(obj);
        }

        // Extract form_id (strip f: prefix) and form_name from first data row
        const firstRow  = rows[0] || {};
        const rawFormId = firstRow['form_id'] || '';
        const formId    = rawFormId.replace(/^f:/i, '').trim();
        const formName  = (firstRow['form_name'] || '').replace(/^"|"$/g, '').trim();

        resolve({ headers, rows, formId, formName });
      } catch (err) {
        reject(err);
      }
    };
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsText(file, 'UTF-16');
  });

// ─── Sample cell value from raw rows (first non-empty across first 3 rows) ───
const sampleValue = (rows, col) => {
  for (const row of rows.slice(0, 3)) {
    const v = (row[col] || '').trim();
    if (v) return v.length > 40 ? v.slice(0, 40) + '…' : v;
  }
  return '—';
};

// ─── Main component ───────────────────────────────────────────────────────────
const LeadImport = () => {
  const fileInputRef = useRef(null);

  const [step, setStep]           = useState('upload');  // upload | preview | results
  const [file, setFile]           = useState(null);
  const [parsedData, setParsedData]     = useState(null); // {headers, rows, formId, formName}
  const [mappings, setMappings]         = useState({});
  const [showMapping, setShowMapping]   = useState(false); // true = new form, needs mapping UI
  const [savingMappings, setSavingMappings] = useState(false);

  const [processedRows, setProcessedRows]   = useState([]);
  const [validation, setValidation]         = useState(null); // {existing_leads, existing_customers}
  const [skipExisting, setSkipExisting]     = useState(true);

  const [importing, setImporting]     = useState(false);
  const [importResult, setImportResult] = useState(null);

  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');

  // ── File selection ──────────────────────────────────────────────────────────
  const handleFileChange = async (e) => {
    const f = e.target.files[0];
    if (!f) return;
    processFile(f);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const f = e.dataTransfer.files[0];
    if (f) processFile(f);
  };

  const processFile = async (f) => {
    setFile(f);
    setError('');
    setLoading(true);
    try {
      const parsed = await parseMetaFile(f);
      setParsedData(parsed);

      if (!parsed.formId) {
        // No form_id column — build default mappings and show mapping UI
        const defaultMaps = {};
        parsed.headers.filter(h => !META_SYSTEM_COLS.has(h.toLowerCase())).forEach(h => { defaultMaps[h] = autoSuggest(h); });
        setMappings(defaultMaps);
        setShowMapping(true);
        setLoading(false);
        return;
      }

      // Try to load saved mappings for this form
      const res = await leadsService.getFormMappings(parsed.formId);
      if (res.data.length > 0) {
        // Known form — auto-apply saved mappings
        const maps = {};
        res.data.forEach(m => { maps[m.csv_column] = m.target_field; });
        // Fill any new columns not in saved mappings
        parsed.headers.filter(h => !META_SYSTEM_COLS.has(h.toLowerCase())).forEach(h => { if (!(h in maps)) maps[h] = autoSuggest(h); });
        setMappings(maps);
        setShowMapping(false);
        await buildPreview(parsed, maps);
      } else {
        // Unknown form — build auto-suggestions and show mapping UI
        const defaultMaps = {};
        parsed.headers.filter(h => !META_SYSTEM_COLS.has(h.toLowerCase())).forEach(h => { defaultMaps[h] = autoSuggest(h); });
        setMappings(defaultMaps);
        setShowMapping(true);
      }
    } catch (err) {
      setError('Failed to parse file: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // ── Build preview rows and validate against DB ──────────────────────────────
  const buildPreview = async (parsed, maps) => {
    setLoading(true);
    try {
      const data = parsed || parsedData;
      const mapsToUse = maps || mappings;

      // Process all rows
      const phonesSeen = new Set();
      const processed  = data.rows.map(row => {
        const clean = applyMappings(row, mapsToUse);
        let status = 'valid';
        if (!clean.phone)                         status = 'invalid_phone';
        else if (phonesSeen.has(clean.phone))     status = 'duplicate_in_file';
        else                                       phonesSeen.add(clean.phone);
        return { ...clean, _status: status };
      });
      setProcessedRows(processed);

      // Validate against DB
      const validPhones   = [...new Set(processed.filter(r => r._status === 'valid').map(r => r.phone))];
      const uniquePincodes = [...new Set(processed.map(r => r.pincode).filter(Boolean))];
      const valRes = await leadsService.validateImportPhones(validPhones, uniquePincodes);
      setValidation(valRes.data);

      setStep('preview');
    } catch (err) {
      setError('Failed to prepare preview: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // ── Save mappings then go to preview ───────────────────────────────────────
  const handleSaveMappings = async () => {
    setSavingMappings(true);
    try {
      const mappingsList = Object.entries(mappings).map(([csv_column, target_field]) => ({
        csv_column, target_field,
      }));
      await leadsService.saveFormMappings(parsedData.formId, parsedData.formName, mappingsList);
      setShowMapping(false);
      await buildPreview(null, null);
    } catch (err) {
      setError('Failed to save mappings: ' + err.message);
    } finally {
      setSavingMappings(false);
    }
  };

  // ── Run import ──────────────────────────────────────────────────────────────
  const handleImport = async () => {
    setImporting(true);
    setError('');
    try {
      const existingLeads        = new Set(validation?.existing_leads         || []);
      const existingCustomers    = new Set(validation?.existing_customers    || []);
      const unserviceablePincodes = new Set(validation?.unserviceable_pincodes || []);

      const rowsToSend = processedRows
        .filter(r => {
          if (r._status === 'invalid_phone')                              return false;
          if (r._status === 'duplicate_in_file')                         return false;
          if (existingCustomers.has(r.phone))                            return false;
          if (r.pincode && unserviceablePincodes.has(r.pincode))         return false;
          return true;
        })
        .map(({ _status, whatsapp, ...rest }) => rest);

      const res = await leadsService.importLeads({
        rows: rowsToSend,
        filename: file?.name,
        form_id: parsedData?.formId,
        form_name: parsedData?.formName,
        skip_existing: skipExisting,
      });

      setImportResult(res.data);
      setStep('results');
    } catch (err) {
      setError('Import failed: ' + err.message);
    } finally {
      setImporting(false);
    }
  };

  const handleReset = () => {
    setStep('upload');
    setFile(null);
    setParsedData(null);
    setMappings({});
    setShowMapping(false);
    setProcessedRows([]);
    setValidation(null);
    setImportResult(null);
    setError('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // ── Stats helpers ───────────────────────────────────────────────────────────
  const stats = (() => {
    if (!processedRows.length) return null;
    const existingLeads    = new Set(validation?.existing_leads    || []);
    const existingCustomers = new Set(validation?.existing_customers || []);
    const unserviceable = new Set(validation?.unserviceable_pincodes || []);
    const invalid       = processedRows.filter(r => r._status === 'invalid_phone').length;
    const dupInFile     = processedRows.filter(r => r._status === 'duplicate_in_file').length;
    const validRows     = processedRows.filter(r => r._status === 'valid');
    const dupInDB       = validRows.filter(r => existingLeads.has(r.phone) || existingCustomers.has(r.phone)).length;
    const badPin        = validRows.filter(r => r.pincode && unserviceable.has(r.pincode)).length;
    const willImport    = validRows.length - (skipExisting ? dupInDB : existingCustomers.size) - badPin;
    return { total: processedRows.length, invalid, dupInFile, dupInDB, badPin, willImport: Math.max(0, willImport) };
  })();

  // ─── Render ────────────────────────────────────────────────────────────────

  // Step indicator
  const steps = ['Upload', 'Preview', 'Done'];
  const stepIndex = step === 'upload' ? 0 : step === 'preview' ? 1 : 2;

  return (
    <div className="lead-import">
      {/* Header */}
      <div className="section-header">
        <div>
          <h2>Import Leads — Meta Ads</h2>
          <p>Upload CSV exports from Meta Ads Manager to bulk-create leads</p>
        </div>
        <Link to="/leads" className="btn-cancel" style={{ textDecoration: 'none', padding: '0.5rem 1rem' }}>
          ← Back to Leads
        </Link>
      </div>

      {/* Step indicators */}
      <div className="import-step-bar">
        {steps.map((s, i) => (
          <div key={s} className={`import-step-item ${i === stepIndex ? 'active' : ''} ${i < stepIndex ? 'done' : ''}`}>
            <div className="import-step-dot">{i < stepIndex ? '✓' : i + 1}</div>
            <span className="import-step-label">{s}</span>
            {i < steps.length - 1 && <div className="import-step-line" />}
          </div>
        ))}
      </div>

      {error && <div className="error-message" style={{ margin: '0 0 1rem' }}>{error}</div>}

      {/* ── STEP 1: Upload ── */}
      {step === 'upload' && (
        <div className="import-card">
          {/* Drop zone */}
          {!file && (
            <div
              className="import-dropzone"
              onClick={() => fileInputRef.current?.click()}
              onDrop={handleDrop}
              onDragOver={e => e.preventDefault()}
            >
              <div className="import-dropzone-icon">📂</div>
              <p className="import-dropzone-title">Drop Meta Ads CSV here or click to browse</p>
              <p className="import-dropzone-hint">Export from Meta Ads Manager → Leads → Download</p>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,.tsv,.txt"
                style={{ display: 'none' }}
                onChange={handleFileChange}
              />
            </div>
          )}

          {loading && (
            <div className="import-loading">
              <div className="import-spinner" />
              <p>Parsing file…</p>
            </div>
          )}

          {/* Column mapping — shown when form is unknown */}
          {!loading && file && showMapping && parsedData && (
            <>
              <div className="import-mapping-header">
                <div>
                  <h3 className="import-section-title">New Form Detected — Map Columns</h3>
                  <p className="import-section-sub">
                    Form: <strong>{parsedData.formName || parsedData.formId || 'unknown'}</strong>
                    &nbsp;· This mapping will be saved and applied automatically next time.
                  </p>
                </div>
                <button className="btn-cancel" onClick={handleReset}>Change File</button>
              </div>

              <div className="import-mapping-table-wrap">
                <table className="import-mapping-table">
                  <thead>
                    <tr>
                      <th>CSV Column</th>
                      <th>Sample Data</th>
                      <th>Maps To</th>
                    </tr>
                  </thead>
                  <tbody>
                    {parsedData.headers.filter(col => !META_SYSTEM_COLS.has(col.toLowerCase())).map(col => (
                      <tr key={col}>
                        <td className="import-col-name">{col}</td>
                        <td className="import-col-sample">{sampleValue(parsedData.rows, col)}</td>
                        <td>
                          <select
                            className="import-field-select"
                            value={mappings[col] || 'ignore'}
                            onChange={e => setMappings(prev => ({ ...prev, [col]: e.target.value }))}
                          >
                            {TARGET_FIELDS.map(f => (
                              <option key={f.value} value={f.value}>{f.label}</option>
                            ))}
                          </select>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="import-mapping-actions">
                <button className="btn-cancel" onClick={handleReset}>Cancel</button>
                <button className="btn-submit" onClick={handleSaveMappings} disabled={savingMappings}>
                  {savingMappings ? 'Saving…' : 'Save Mapping & Preview →'}
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {/* ── STEP 2: Preview ── */}
      {step === 'preview' && processedRows.length > 0 && (
        <div className="import-card">
          <div className="import-preview-header">
            <div>
              <h3 className="import-section-title">Preview</h3>
              <p className="import-section-sub">
                File: <strong>{file?.name}</strong> · Form: <strong>{parsedData?.formName || parsedData?.formId}</strong>
              </p>
            </div>
            <button className="btn-cancel" onClick={handleReset}>Change File</button>
          </div>

          {/* Stats */}
          {stats && (
            <div className="import-stats-row">
              <div className="import-stat import-stat--total">
                <span className="import-stat-num">{stats.total}</span>
                <span className="import-stat-lbl">Total rows</span>
              </div>
              <div className="import-stat import-stat--good">
                <span className="import-stat-num">{stats.total - stats.invalid - stats.dupInFile}</span>
                <span className="import-stat-lbl">Valid phones</span>
              </div>
              {stats.invalid > 0 && (
                <div className="import-stat import-stat--warn">
                  <span className="import-stat-num">{stats.invalid}</span>
                  <span className="import-stat-lbl">Invalid phone</span>
                </div>
              )}
              {stats.dupInFile > 0 && (
                <div className="import-stat import-stat--warn">
                  <span className="import-stat-num">{stats.dupInFile}</span>
                  <span className="import-stat-lbl">Duplicate in file</span>
                </div>
              )}
              {stats.dupInDB > 0 && (
                <div className="import-stat import-stat--db">
                  <span className="import-stat-num">{stats.dupInDB}</span>
                  <span className="import-stat-lbl">Already in DB</span>
                </div>
              )}
              {stats.badPin > 0 && (
                <div className="import-stat import-stat--warn">
                  <span className="import-stat-num">{stats.badPin}</span>
                  <span className="import-stat-lbl">Pincode not serviceable</span>
                </div>
              )}
            </div>
          )}

          {/* Skip existing toggle */}
          {validation && (validation.existing_leads.length > 0) && (
            <div className="import-dedup-row">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={skipExisting}
                  onChange={e => setSkipExisting(e.target.checked)}
                />
                Skip leads already in the system ({validation.existing_leads.length} existing)
              </label>
            </div>
          )}

          {/* Data table */}
          <div className="import-preview-table-wrap">
            <table className="orders-table import-preview-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Status</th>
                  <th>Name</th>
                  <th>Phone</th>
                  <th>Area</th>
                  <th>Pincode</th>
                  <th>Notes</th>
                  <th>Source Ref</th>
                </tr>
              </thead>
              <tbody>
                {processedRows.map((row, i) => {
                  const existLead = validation?.existing_leads?.includes(row.phone);
                  const existCust = validation?.existing_customers?.includes(row.phone);
                  const badPincode = row.pincode && validation?.unserviceable_pincodes?.includes(row.pincode);
                  let rowClass = '';
                  let badge = null;
                  if (row._status === 'invalid_phone') {
                    rowClass = 'import-row--skip';
                    badge = <span className="import-badge import-badge--error">Invalid phone</span>;
                  } else if (row._status === 'duplicate_in_file') {
                    rowClass = 'import-row--skip';
                    badge = <span className="import-badge import-badge--warn">Dup in file</span>;
                  } else if (existCust) {
                    rowClass = 'import-row--skip';
                    badge = <span className="import-badge import-badge--warn">Customer exists</span>;
                  } else if (existLead) {
                    rowClass = skipExisting ? 'import-row--skip' : '';
                    badge = <span className="import-badge import-badge--db">{skipExisting ? 'Lead exists — skip' : 'Lead exists — import'}</span>;
                  } else if (badPincode) {
                    rowClass = 'import-row--skip';
                    badge = <span className="import-badge import-badge--warn">Pincode not serviceable</span>;
                  } else {
                    badge = <span className="import-badge import-badge--ok">New</span>;
                  }
                  return (
                    <tr key={i} className={rowClass}>
                      <td style={{ color: '#999', fontSize: '0.8rem' }}>{i + 1}</td>
                      <td>{badge}</td>
                      <td>{row.name || <em style={{ color: '#aaa' }}>—</em>}</td>
                      <td style={{ fontFamily: 'monospace' }}>{row.phone || <em style={{ color: '#e74c3c' }}>missing</em>}</td>
                      <td>{row.area || '—'}</td>
                      <td>
                        {row.pincode || '—'}
                        {badPincode && (
                          <span className="import-badge import-badge--warn" style={{ marginLeft: '0.4rem' }}>not serviceable</span>
                        )}
                      </td>
                      <td style={{ maxWidth: '180px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.notes || '—'}</td>
                      <td style={{ maxWidth: '180px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.source_ref || '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="import-preview-actions">
            <button className="btn-cancel" onClick={handleReset}>Cancel</button>
            <button className="btn-submit" onClick={handleImport} disabled={importing}>
              {importing
                ? 'Importing…'
                : `Import ${stats?.willImport ?? '?'} Lead${stats?.willImport !== 1 ? 's' : ''} →`}
            </button>
          </div>
        </div>
      )}

      {/* ── STEP 3: Results ── */}
      {step === 'results' && importResult && (
        <div className="import-card">
          <h3 className="import-section-title">Import Complete</h3>
          <div className="import-result-grid">
            <div className="import-result-item import-result--green">
              <span className="import-result-num">{importResult.imported}</span>
              <span className="import-result-lbl">Leads Created</span>
            </div>
            <div className="import-result-item import-result--grey">
              <span className="import-result-num">{importResult.skipped}</span>
              <span className="import-result-lbl">Skipped</span>
            </div>
            {importResult.errors > 0 && (
              <div className="import-result-item import-result--red">
                <span className="import-result-num">{importResult.errors}</span>
                <span className="import-result-lbl">Errors</span>
              </div>
            )}
          </div>

          {importResult.errors > 0 && importResult.error_details?.length > 0 && (
            <div className="import-errors-section">
              <p className="import-section-title" style={{ fontSize: '0.9rem' }}>Error Details</p>
              {importResult.error_details.map((e, i) => (
                <div key={i} className="import-error-row">
                  <span style={{ fontFamily: 'monospace' }}>{e.phone}</span>
                  <span style={{ color: '#e74c3c', marginLeft: '1rem' }}>{e.error}</span>
                </div>
              ))}
            </div>
          )}

          <div className="import-preview-actions">
            <button className="btn-cancel" onClick={handleReset}>Import Another File</button>
            <Link to="/leads" className="btn-submit" style={{ textDecoration: 'none' }}>
              Go to Leads →
            </Link>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeadImport;
